// =============================================================================
// IntegrationStatusBanner.tsx — degraded-mode warning banner
// =============================================================================
// Linear: CLINIO-195 (SHARED-5)
//
// Renders a yellow warning banner at the top of any page that depends on
// Clinio Pay or Clinio Forms when the relevant integration is unreachable.
// Returns null when the integration is healthy (or the clinic has no active
// tenant for that service).
//
// Usage:
//   <IntegrationStatusBanner integration="pay" />
//   <IntegrationStatusBanner integration="forms" />
// =============================================================================

import { AlertTriangle } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useClinicPayStatus } from '@/hooks/useClinicPay';
import { useFormsTemplates, useFormsTenantStatus } from '@/hooks/useClinioForms';

// ── Props ─────────────────────────────────────────────────────────────────────

interface IntegrationStatusBannerProps {
  integration: 'pay' | 'forms';
  className?: string;
}

// ── Messages ──────────────────────────────────────────────────────────────────

const MESSAGES = {
  pay: 'Betalingsservice midlertidigt utilgængelig. Fakturaer kan stadig oprettes; betalingslinks og Stripe-tilknytning er sat på pause.',
  forms: 'Spørgeskemaservice midlertidigt utilgængelig. Prøv igen om lidt.',
} as const;

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Shows a yellow warning banner when Clinio Pay or Clinio Forms is unreachable.
 * Renders nothing when the integration is healthy.
 *
 * Availability is derived from TanStack Query error state:
 *   - Pay:   `useClinicPayStatus` errors when the `pay-status` Edge Function
 *            cannot reach Clinio Pay (circuit breaker open → 502).
 *   - Forms: `useFormsTemplates` errors when `forms-list-templates` cannot
 *            reach Clinio Forms, but only when the clinic has an active
 *            Forms tenant (inactive clinics are not affected).
 */
export function IntegrationStatusBanner({ integration, className }: IntegrationStatusBannerProps) {
  const { activeClinicId } = useActiveClinic();

  // Always call both hooks unconditionally (Rules of Hooks).
  const payQuery    = useClinicPayStatus(activeClinicId);
  const formsStatus = useFormsTenantStatus(activeClinicId);
  const formsQuery  = useFormsTemplates(activeClinicId);

  const isPayUnavailable =
    payQuery.isError &&
    // Only surface a banner for actual service-level failures, not auth/config issues.
    payQuery.error instanceof Error &&
    (payQuery.error.message.includes('clinio-pay-unavailable') ||
      payQuery.error.message.includes('502') ||
      payQuery.error.message.includes('unavailable') ||
      payQuery.error.message.includes('circuit'));

  const isFormsUnavailable =
    formsStatus.data?.status === 'active' &&
    formsQuery.isError &&
    formsQuery.error instanceof Error &&
    (formsQuery.error.message.includes('clinio-forms-unavailable') ||
      formsQuery.error.message.includes('502') ||
      formsQuery.error.message.includes('unavailable') ||
      formsQuery.error.message.includes('circuit'));

  const isUnavailable = integration === 'pay' ? isPayUnavailable : isFormsUnavailable;

  if (!isUnavailable) return null;

  return (
    <div
      className={[
        'flex items-start gap-3 rounded-lg border-l-4 border-ds-amber-line bg-ds-amber-tint p-4',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="alert"
      aria-live="polite"
    >
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-ds-amber" />
      <p className="text-sm text-ds-amber-ink">{MESSAGES[integration]}</p>
    </div>
  );
}
